/**
 * SQLiteHistoryRepository — mobile implementation of HistoryRepository.
 *
 * Stores durable transfer history in native SQLite via react-native-quick-sqlite.
 * Falls back to InMemoryDB on web/test environments (where native SQLite is unavailable).
 *
 * revision guard: writes are discarded if stored revision >= incoming revision.
 * This prevents stale progress checkpoints from overwriting terminal states.
 */

import type { HistoryRepository, PersistedSession, ResumeData } from '@share/domain';
import { filterInterruptedSessions, filterPausedReceiveSessions } from './history-filters';

import { getDBConnection } from './sqlite-client';

export class SQLiteHistoryRepository implements HistoryRepository {

  async saveSession(session: PersistedSession): Promise<void> {
    const conn = getDBConnection();

    // Revision guard: fetch current revision and discard stale writes
    const existing = conn.execute(
      `SELECT revision FROM transfers WHERE id = ?;`,
      [session.id]
    );
    const currentRevision: number = existing.rows._array[0]?.revision ?? -1;
    if (currentRevision >= session.revision) {
      return; // Stale checkpoint — discard silently
    }

    const query = `
      REPLACE INTO transfers (
        id, direction, filesJson, status, progress,
        bytesTransferred, bytesUploaded, totalBytes,
        magnetUri, destinationRef, resumeDataJson,
        torrentIdentityJson,
        revision, createdAt, updatedAt
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);
    `;
    conn.execute(query, [
      session.id,
      session.direction,
      JSON.stringify(session.files),
      session.status,
      session.progress,
      session.bytesTransferred,
      session.bytesUploaded ?? 0,
      session.totalBytes,
      session.magnetUri ?? null,
      session.destinationRef ?? null,
      session.resumeData ? JSON.stringify(session.resumeData) : null,
      JSON.stringify(session.torrentIdentity),  // always persist identity
      session.revision,
      session.createdAt,
      session.updatedAt,
    ]);
  }

  async getSession(id: string): Promise<PersistedSession | null> {
    const conn = getDBConnection();
    const result = conn.execute(`SELECT * FROM transfers WHERE id = ?;`, [id]);
    const row = result.rows._array[0];
    return row ? this._rowToSession(row) : null;
  }

  async getAllSessions(): Promise<PersistedSession[]> {
    const conn = getDBConnection();
    const result = conn.execute(`SELECT * FROM transfers ORDER BY createdAt DESC;`);
    return result.rows._array.map((row: any) => this._rowToSession(row));
  }

  async deleteSession(id: string): Promise<void> {
    const conn = getDBConnection();
    conn.execute(`DELETE FROM transfers WHERE id = ?;`, [id]);
  }

  async updateResumeData(sessionId: string, resumeData: ResumeData): Promise<void> {
    const conn = getDBConnection();
    conn.execute(
      `UPDATE transfers SET resumeDataJson = ? WHERE id = ?;`,
      [JSON.stringify(resumeData), sessionId]
    );
  }

  async getInterruptedSessions(): Promise<PersistedSession[]> {
    const all = await this.getAllSessions();
    return filterInterruptedSessions(all);
  }

  async getPausedReceiveSessions(): Promise<PersistedSession[]> {
    const all = await this.getAllSessions();
    return filterPausedReceiveSessions(all);
  }

  private _rowToSession(row: any): PersistedSession {
    // Resolve torrentIdentity with migration fallback for pre-refactor rows:
    //   1. Parse stored torrentIdentityJson (normal path after migration)
    //   2. Extract v1 inline from magnetUri (legacy rows — one-liner, NOT parseMagnet)
    //   3. Use session.id if it looks like a 40-char infoHash (very old sessions)
    //   4. Empty object as last resort — session will be skipped during recovery
    let torrentIdentity: import('@share/domain').TorrentIdentity;
    if (row.torrentIdentityJson) {
      torrentIdentity = JSON.parse(row.torrentIdentityJson);
    } else if (row.magnetUri) {
      const match = /urn:btih:([0-9a-f]{40})/i.exec(row.magnetUri);
      torrentIdentity = match ? { v1: match[1].toLowerCase() } : {};
    } else if (typeof row.id === 'string' && /^[0-9a-f]{40}$/.test(row.id)) {
      torrentIdentity = { v1: row.id.toLowerCase() };
    } else {
      torrentIdentity = {};
    }

    return {
      id: row.id,
      torrentIdentity,
      direction: row.direction,
      files: JSON.parse(row.filesJson || '[]'),
      status: row.status,
      progress: row.progress,
      bytesTransferred: row.bytesTransferred,
      bytesUploaded: row.bytesUploaded ?? 0,
      totalBytes: row.totalBytes,
      magnetUri: row.magnetUri ?? undefined,
      destinationRef: row.destinationRef ?? undefined,
      resumeData: row.resumeDataJson ? JSON.parse(row.resumeDataJson) : undefined,
      revision: row.revision ?? 0,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt ?? row.createdAt,
    };
  }
}

export const SQLiteHistory = new SQLiteHistoryRepository();
